// Componente raiz: maneja la sesion (AuthProvider) y la navegacion entre
// las tres vistas principales: lista de empresas, documentos plantilla
// ("Documentos Originales") y documentos de una empresa concreta.
import { useState, useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { DocsProvider, useDocs } from "./context/DocsContext";
import LoginPage from "./components/LoginPage";
import Sidebar from "./components/Sidebar";
import Header from "./components/Header";
import CompanyGrid from "./components/CompanyGrid";
import FolderGrid from "./components/FolderGrid";
import DocumentGrid from "./components/DocumentGrid";
import UploadModal from "./components/UploadModal";
import FileEditor from "./components/FileEditor";
import { Loader } from "lucide-react";
import "./styles.css";

function DocsView({ company, onBack, onHome, isTemplates }) {
  const [activeFolder, setActiveFolder] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [search, setSearch] = useState("");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= 900;
  });
  const { fetchDocs } = useDocs();

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  useEffect(() => {
    const syncMobile = () => {
      const mobile = window.innerWidth <= 900;
      setIsMobile(mobile);
      if (!mobile) setMobileNavOpen(false);
    };

    syncMobile();
    window.addEventListener("resize", syncMobile);
    return () => window.removeEventListener("resize", syncMobile);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("nav-open", isMobile && mobileNavOpen);
    return () => document.body.classList.remove("nav-open");
  }, [isMobile, mobileNavOpen]);

  const enterFolder = (f) => { setActiveFolder(f); setSearch(""); };
  const exitFolder = () => { setActiveFolder(null); setSearch(""); };
  const closeMobileNav = () => setMobileNavOpen(false);

  return (
    <>
      <Sidebar
        company={company}
        isTemplates={isTemplates}
        mobileOpen={mobileNavOpen}
        onCloseMobile={closeMobileNav}
      />
      <div className="main-area">
        <Header
          search={search}
          onSearch={setSearch}
          onUpload={() => setShowUpload(true)}
          company={company}
          folder={activeFolder}
          onBack={activeFolder ? exitFolder : onBack}
          onHome={onHome}
          isTemplates={isTemplates}
          onOpenMobileNav={() => setMobileNavOpen(true)}
        />
        {activeFolder ? (
          <DocumentGrid category={activeFolder} search={search} onEdit={setEditingDoc} />
        ) : (
          <FolderGrid search={search} onEnter={enterFolder} />
        )}
      </div>
      <AnimatePresence>
        {showUpload && <UploadModal key="upload" category={activeFolder} company={company} isTemplates={isTemplates} onClose={() => setShowUpload(false)} />}
        {editingDoc && <FileEditor key="editor" doc={editingDoc} onClose={() => setEditingDoc(null)} />}
      </AnimatePresence>
    </>
  );
}

function AppContent() {
  const { user, loading } = useAuth();
  const [view, setView] = useState("companies");
  const [activeCompany, setActiveCompany] = useState(null);
  const [search, setSearch] = useState("");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= 900;
  });

  useEffect(() => {
    const syncMobile = () => {
      const mobile = window.innerWidth <= 900;
      setIsMobile(mobile);
      if (!mobile) setMobileNavOpen(false);
    };

    syncMobile();
    window.addEventListener("resize", syncMobile);
    return () => window.removeEventListener("resize", syncMobile);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("nav-open", isMobile && mobileNavOpen);
    return () => document.body.classList.remove("nav-open");
  }, [isMobile, mobileNavOpen]);

  if (loading) {
    return (
      <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Loader size={32} className="spin" opacity={0.4} />
      </div>
    );
  }

  if (!user) return <LoginPage />;

  const goToCompanies = () => { setView("companies"); setActiveCompany(null); setSearch(""); setMobileNavOpen(false); };
  const goToTemplates = () => { setView("templates"); setActiveCompany(null); setSearch(""); setMobileNavOpen(false); };
  const goToCompany = (company) => { setView("company"); setActiveCompany(company); setSearch(""); };

  if (view === "templates") {
    return (
      <DocsProvider companyId={null}>
        <div className="app-layout">
          <DocsView isTemplates onBack={goToCompanies} onHome={goToCompanies} />
        </div>
      </DocsProvider>
    );
  }

  if (view === "company" && activeCompany) {
    return (
      <DocsProvider companyId={activeCompany.id}>
        <div className="app-layout">
          <DocsView company={activeCompany} onBack={goToCompanies} onHome={goToCompanies} />
        </div>
      </DocsProvider>
    );
  }

  return (
    <DocsProvider companyId={null}>
      <div className="app-layout">
        <Sidebar
          onGoTemplates={goToTemplates}
          mobileOpen={mobileNavOpen}
          onCloseMobile={() => setMobileNavOpen(false)}
        />
        <div className="main-area">
          <Header
            search={search}
            onSearch={setSearch}
            onOpenMobileNav={() => setMobileNavOpen(true)}
          />
          <CompanyGrid search={search} onEnter={goToCompany} />
        </div>
      </div>
    </DocsProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
